import { Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import HeroSection from './components/HeroSection';
import FeaturedCategories from './components/FeaturedCategories';
import ArticleCard from '@/components/feature/ArticleCard';
import SEOHead from '@/components/base/SEOHead';

export default function Home() {
  const featuredArticles = articles.filter((a) => a.featured).slice(0, 6);
  const latestArticles = articles.slice(0, 8);
  const popularArticles = [...articles].sort((a, b) => b.views - a.views).slice(0, 4);

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'po文网',
    url: 'https://arkfilm.cn',
    description: 'po文网是专注于原创内容创作与文章发布的综合性平台，汇聚海量优质创作者，支持多类型文章po出与分享。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://arkfilm.cn/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'po文网',
    url: 'https://arkfilm.cn',
    description: '原创文章发布与内容创作平台',
    foundingDate: '2024',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@arkfilm.cn',
    },
  };

  return (
    <>
      <SEOHead
        title="首页"
        description="po文网是专注于原创内容创作与文章发布的综合性平台，汇聚海量优质创作者，支持多类型文章po出与分享。无论你是写作新手还是资深创作者，po文网都能为你提供便捷的发文工具、精准的读者匹配与良好的创作生态。"
        keywords="po文网,po文网注册,po文网创作者,po文网原创文章,po文网发布平台,po文网写作社区,在线po文工具"
        canonicalUrl="https://arkfilm.cn"
        schema={{ ...orgSchema }}
      />

      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>

      <HeroSection />

      <FeaturedCategories />

      {/* Featured Articles */}
      <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-10">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-2">
                精选好文
              </h2>
              <p className="text-sm text-foreground-600">编辑团队精心挑选的优质内容</p>
            </div>
            <Link
              to="/category"
              className="hidden sm:flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200 whitespace-nowrap"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {featuredArticles.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>

          <Link
            to="/category"
            className="sm:hidden flex items-center justify-center gap-1 mt-6 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200 whitespace-nowrap"
          >
            查看全部文章 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      {/* Why po文网 */}
      <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
              为什么选择 po文网？
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
              我们致力于为每一位创作者提供最好的写作与分享体验
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: 'ri-pencil-ruler-2-line', title: '专业编辑器', desc: 'Markdown与富文本双模式，所见即所得的沉浸式写作体验' },
              { icon: 'ri-user-heart-line', title: '精准读者匹配', desc: '智能推荐算法让你的文章精准触达感兴趣的目标读者' },
              { icon: 'ri-line-chart-line', title: '数据洞察', desc: '详尽的阅读数据分析，助你了解读者偏好，持续优化内容' },
              { icon: 'ri-shield-check-line', title: '原创保护', desc: 'AI驱动的原创检测系统，全方位保护你的知识产权' },
            ].map((item, idx) => (
              <div key={idx} className="text-center p-5">
                <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mx-auto mb-4">
                  <i className={`${item.icon} text-2xl`}></i>
                </div>
                <h3 className="font-medium text-foreground-900 mb-2">{item.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Articles */}
      <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-10">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-2">
                最新发布
              </h2>
              <p className="text-sm text-foreground-600">发现来自创作者的最新内容</p>
            </div>
            <Link
              to="/category"
              className="hidden sm:flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200 whitespace-nowrap"
            >
              更多文章 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {latestArticles.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>

          <Link
            to="/category"
            className="sm:hidden flex items-center justify-center gap-1 mt-6 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200 whitespace-nowrap"
          >
            更多文章 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      {/* Popular / Hot */}
      <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-6">
                热门文章
              </h2>
              <div className="space-y-4">
                {popularArticles.map((article, idx) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="flex items-start gap-4 p-4 rounded-lg bg-background-100 hover:bg-primary-50 border border-transparent hover:border-primary-200 transition-all duration-200 group cursor-pointer"
                  >
                    <span className="text-2xl font-bold text-primary-400 w-8 flex-shrink-0">
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs font-medium rounded-md whitespace-nowrap">
                          {article.categoryName}
                        </span>
                      </div>
                      <h3 className="font-medium text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 line-clamp-1">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 line-clamp-1 mt-1">{article.excerpt}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-foreground-500">
                        <span>{article.author.name}</span>
                        <span>{article.views.toLocaleString()} 阅读</span>
                        <span>{article.likes} 赞</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            <div>
              <div className="bg-gradient-to-br from-primary-500 to-primary-700 rounded-lg p-6 text-background-50">
                <h3 className="font-heading text-xl mb-3">加入创作者社区</h3>
                <p className="text-sm text-background-50/80 leading-relaxed mb-5">
                  在这里发布你的原创文章，与千万读者分享你的见解，让好内容被发现、被传播、被认可。
                </p>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-50 text-primary-600 text-sm font-medium hover:bg-background-100 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-quill-pen-line"></i>
                  立即加入
                </Link>
              </div>

              <div className="mt-6 bg-background-100 rounded-lg p-5">
                <h4 className="font-medium text-foreground-900 mb-3 flex items-center gap-2">
                  <i className="ri-price-tag-3-line text-accent-500"></i>
                  热门标签
                </h4>
                <div className="flex flex-wrap gap-2">
                  {['人工智能', '职场成长', '学习方法', '成都美食', '云南旅游', '敦煌', '摄影', '独居生活', '中医养生', '城市漫步'].map((tag) => (
                    <Link
                      key={tag}
                      to="/category"
                      className="px-3 py-1 bg-background-50 text-xs text-foreground-700 rounded-full border border-background-200 hover:border-primary-300 hover:text-primary-600 transition-all duration-200 whitespace-nowrap"
                    >
                      {tag}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}